/**
 * Alameda Soda skin for the public new-store form.
 *
 * WHY A LOCAL SKIN. The portal is Brix navy end to end. This one page is the
 * first thing a franchisee or a new chain store ever sees from us, and Sky
 * chose the Alameda Soda look for it — the consumer brand whose cans they'll
 * actually be pouring. Re-skinning one public page by scoping tokens to a
 * wrapper keeps that decision from leaking into the signed-in app.
 *
 * ⚠ THE BRAND RED IS DELIBERATELY ABSENT. Alameda Soda's primary is #C8102E,
 * and two independent rules say don't ship it here:
 *   1. this repo's don't-touch list bans that hex outright, and
 *   2. the brand rules themselves call it a WORKING value, "do NOT ship
 *      without confirming against the official 2024 logo".
 * So the palette is built from the rest of the Alameda Soda system — cream
 * ground, ink, Hangar steel, ginger gold — which is recognizably the brand
 * without the one colour nobody has cleared. Hangar steel carries the primary
 * actions. If the red is later verified AND unbanned, changing `--as-primary`
 * below is the whole edit.
 *
 * Typography note: the brand calls for a display serif (Recoleta / Tiempos).
 * Pulling a new webfont into the portal bundle for a single page isn't worth
 * the payload, so this leans on the existing display face with brand-ish
 * scale, weight and tracking. Swapping in the real face is a font-family line.
 */

/** Scope every token to one class so the rest of the app is untouched. */
export const APPLY_CHAIN_THEME_CLASS = 'as-skin';

export function AlamedaSodaTheme() {
  return (
    <style>{`
      .${APPLY_CHAIN_THEME_CLASS} {
        /* Hangar steel — primary actions. NOT the brand red (see file note). */
        --as-primary: #3A3F45;
        --as-primary-hover: #2B2F34;
        /* Ginger gold — highlights, step markers, selected states. */
        --as-accent: #D4A24C;
        --as-accent-soft: #F6ECD8;
        --as-cream: #F4EFE3;
        --as-surface: #FFFFFF;
        --as-ink: #1A1A1A;
        --as-muted: #6A6862;
        --as-line: #E2DBCB;
        --as-warning: #8A5A1E;

        background-color: var(--as-cream);
        color: var(--as-ink);
      }

      /* A light paper grain on the ground only — the brand allows a light
         texture on hero backgrounds, never full-bleed over content. */
      .${APPLY_CHAIN_THEME_CLASS} .as-grain::before {
        content: '';
        position: absolute;
        inset: 0;
        pointer-events: none;
        opacity: 0.35;
        background-image: radial-gradient(circle at 1px 1px, rgba(26,26,26,0.05) 1px, transparent 0);
        background-size: 4px 4px;
      }

      .${APPLY_CHAIN_THEME_CLASS} .as-card {
        background: var(--as-surface);
        border: 1px solid var(--as-line);
        border-radius: 16px;
        box-shadow: 0 1px 2px rgba(26,26,26,0.04), 0 12px 32px -18px rgba(26,26,26,0.35);
      }

      .${APPLY_CHAIN_THEME_CLASS} .as-display {
        letter-spacing: -0.018em;
        font-weight: 600;
      }

      .${APPLY_CHAIN_THEME_CLASS} .as-eyebrow {
        letter-spacing: 0.14em;
        text-transform: uppercase;
        font-size: 11px;
        font-weight: 700;
        color: var(--as-muted);
      }

      /* Inputs: the shared ApplyFields components render app-token classes, so
         re-point the few that actually show on this page rather than forking
         the components. */
      .${APPLY_CHAIN_THEME_CLASS} input:not([type='checkbox']):not([type='radio']),
      .${APPLY_CHAIN_THEME_CLASS} textarea,
      .${APPLY_CHAIN_THEME_CLASS} select {
        background: var(--as-surface);
        border-color: var(--as-line);
        color: var(--as-ink);
      }
      .${APPLY_CHAIN_THEME_CLASS} input:focus-visible,
      .${APPLY_CHAIN_THEME_CLASS} textarea:focus-visible,
      .${APPLY_CHAIN_THEME_CLASS} select:focus-visible,
      .${APPLY_CHAIN_THEME_CLASS} button:focus-visible,
      .${APPLY_CHAIN_THEME_CLASS} a:focus-visible {
        outline: 2px solid var(--as-accent);
        outline-offset: 2px;
      }
      .${APPLY_CHAIN_THEME_CLASS} ::placeholder { color: #A9A498; }
      .${APPLY_CHAIN_THEME_CLASS} input[type='checkbox'] { accent-color: var(--as-primary); }

      .${APPLY_CHAIN_THEME_CLASS} .as-btn {
        background: var(--as-primary);
        color: #fff;
        border-radius: 10px;
        font-weight: 600;
        cursor: pointer;
        transition: background-color 180ms ease, opacity 180ms ease;
      }
      .${APPLY_CHAIN_THEME_CLASS} .as-btn:hover:not(:disabled) { background: var(--as-primary-hover); }
      .${APPLY_CHAIN_THEME_CLASS} .as-btn:disabled { opacity: 0.55; cursor: not-allowed; }

      .${APPLY_CHAIN_THEME_CLASS} .as-btn-quiet {
        background: transparent;
        color: var(--as-muted);
        border: 1px solid var(--as-line);
        border-radius: 10px;
        font-weight: 600;
        cursor: pointer;
        transition: color 180ms ease, border-color 180ms ease;
      }
      .${APPLY_CHAIN_THEME_CLASS} .as-btn-quiet:hover:not(:disabled) {
        color: var(--as-ink);
        border-color: var(--as-primary);
      }

      /* The brand red would live here if it were cleared — the gold pill is
         the stand-in for "this is the live step". */
      .${APPLY_CHAIN_THEME_CLASS} .as-step-on { background: var(--as-accent); }
      .${APPLY_CHAIN_THEME_CLASS} .as-step-done { background: var(--as-primary); }
      .${APPLY_CHAIN_THEME_CLASS} .as-step-off { background: var(--as-line); }

      @media (prefers-reduced-motion: reduce) {
        .${APPLY_CHAIN_THEME_CLASS} * { transition-duration: 0.01ms !important; }
      }
    `}</style>
  );
}
